import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CryptoJS from 'crypto-js';
import { AppContext } from '../contexts/AppContext';
import '../styles/ThankYou.css';
import config from '../Config';

const API_BASE_URL = config.api.baseUrl;

// 注意：必須同你後端加密時用嘅一樣！（生產環境建議用環境變數）
const ENCRYPTION_KEY = '12345';

function DThankYou() {
  // 從 URL 取出參數
  const params = new URLSearchParams(window.location.search);
  const encryptedData = params.get('encryptedData');
  const guidFromUrl = params.get('guid'); // ← 新增：直接從 URL 取 guid

  const navigate = useNavigate();
  const { currentLang, setCurrentLang } = useContext(AppContext); // 移除 carParkInfo，因為而家唔使

  // State
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── 語言選單相關 ──
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLangChange = (lang) => {
    setCurrentLang(lang);
    handleClose();
  };

  const getCurrentLangLabel = () => {
    const map = { 'zh-tw': '繁中', 'zh-cn': '简中', 'en': 'Eng' };
    return map[currentLang] || 'Eng';
  };

  const getOtherLanguages = () => [
    { code: 'zh-tw', label: '繁中' },
    { code: 'zh-cn', label: '简中' },
    { code: 'en', label: 'Eng' }
  ].filter(l => l.code !== currentLang);

  // ── 多語言翻譯 ──
  const translations = {
    'zh-tw': {
      loading: '載入中...',
      errorTitle: '載入失敗',
      errorMsg: '無法載入收據，請稍後再試或聯絡管理員',
      title: '付款成功！',
      receiptHeader: '付款詳情',
      carplateLabel: '車牌號碼',
      entryTimeLabel: '入場時間',
      durationLabel: '停泊時間',
      parkingFeeLabel: '總付款金額',
      paymentMethodLabel: '付款方式',
      referenceNumberLabel: '參考編號',
      exitMessage: '請於 15 分鐘內離場。',
      backToHome: '返回主頁',
      invalidGuid: '無效的 GUID，請重新操作',
      hourSingular: '小時',
      hourPlural: '小時',
      minSingular: '分鐘',
      minPlural: '分鐘'
    },
    'zh-cn': {
      loading: '加载中...',
      errorTitle: '加载失败',
      errorMsg: '无法载入收据，请稍后重试或联络管理员',
      title: '支付成功！',
      receiptHeader: '支付详情',
      carplateLabel: '车牌号码',
      entryTimeLabel: '进场时间',
      durationLabel: '停车时长',
      parkingFeeLabel: '总支付金额',
      paymentMethodLabel: '支付方式',
      referenceNumberLabel: '参考编号',
      exitMessage: '请于 15 分钟内离场。',
      backToHome: '返回主页',
      invalidGuid: '无效的 GUID，请重新操作',
      hourSingular: '小时',
      hourPlural: '小时',
      minSingular: '分钟',
      minPlural: '分钟'
    },
    'en': {
      loading: 'Loading...',
      errorTitle: 'Load Failed',
      errorMsg: 'Failed to load receipt. Please try again later or contact administrator.',
      title: 'Payment Successful!',
      receiptHeader: 'PAYMENT DETAILS',
      carplateLabel: 'Licence Plate Number',
      entryTimeLabel: 'Entry Time',
      durationLabel: 'Parking Duration',
      parkingFeeLabel: 'Total Amount',
      paymentMethodLabel: 'Payment Method',
      referenceNumberLabel: 'Reference Number',
      exitMessage: 'Please exit within 15 minutes.',
      backToHome: 'BACK TO HOME',
      invalidGuid: 'Invalid GUID, please try again',
      hourSingular: 'hr',
      hourPlural: 'hrs',
      minSingular: 'min',
      minPlural: 'mins'
    }
  };

  const t = (key) => translations[currentLang]?.[key] || translations['en'][key];

  // ── 主要邏輯：解密 + 呼叫 API（帶 guid 喺 body）──
  useEffect(() => {
    async function loadReceipt() {
      try {
        // 1. 檢查必要參數
        if (!encryptedData) {
          throw new Error('Missing encryptedData in URL');
        }
        if (!guidFromUrl) {
          throw new Error('Missing guid in URL');
        }

        // 2. 解密 encryptedData 取得 LPN 同 EntryTime
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);

        if (!decryptedStr) {
          throw new Error('Decryption failed or empty data');
        }

        const parsed = JSON.parse(decryptedStr);
        const { carplate: lpn, entrytime } = parsed;

        if (!lpn || !entrytime) {
          throw new Error('Decrypted data missing lpn or entrytime');
        }

        console.log('[THANKYOU LOG] Decrypted ->', { lpn, entrytime, guid: guidFromUrl });

        // 3. 呼叫後端 API，body 帶 guid + lpn + entryTime
        const apiUrl = `${API_BASE_URL}/D_GetQrPaymentReceipt`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            guid: guidFromUrl.trim(),           // ← 關鍵：傳 guid 畀後端
            lpn: lpn.trim().toUpperCase(),
            entryTime: entrytime.trim()
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API error ${response.status}: ${errorText}`);
        }

        const result = await response.json();
        console.log('[THANKYOU LOG] API success ->', result);

        setReceiptData(result);
        setLoading(false);
      } catch (err) {
        console.error('[THANKYOU ERROR]', err);
        setError(err.message || 'Unknown error');
        setLoading(false);
      }
    }

    loadReceipt();
  }, [encryptedData, guidFromUrl]); // 依賴 URL 參數

  // ── 時間格式化 ──
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // ── 停留時間格式化 ──
// ── 停留時間格式化（改為分鐘單位）──
const formatDuration = (duration) => {
  if (!duration && duration !== 0) return '';

  const durationNum = parseInt(duration, 10);
  if (isNaN(durationNum) || durationNum < 0) return '';

  const hours = Math.floor(durationNum / 60);
  const mins = durationNum % 60;

  if (hours === 0 && mins === 0) return '0 ' + t('minPlural');

  let result = [];
  if (hours > 0) {
    const hoursStr = hours === 1 ? t('hourSingular') : t('hourPlural');
    result.push(`${hours} ${hoursStr}`);
  }
  if (mins > 0) {
    const minsStr = mins === 1 ? t('minSingular') : t('minPlural');
    result.push(`${mins} ${minsStr}`);
  }

  return result.join(' ');
};

  // ── 語言按鈕 ──
  const renderLangButton = () => (
    <div className="icons">
      <IconButton onClick={handleClick} sx={{ color: '#808080', padding: 0 }}>
        <LanguageIcon sx={{ fontSize: '30px' }} />
      </IconButton>
      <Typography sx={{ ml: 0.5, fontSize: '25px', color: '#808080' }}>
        {getCurrentLangLabel()}
      </Typography>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {getOtherLanguages().map(lang => (
          <MenuItem key={lang.code} onClick={() => handleLangChange(lang.code)}>
            {lang.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  // ── Render ──
  if (loading) {
    return (
      <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 3, fontSize: '1.5rem' }}>{t('loading')}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" className="thank-you-container" sx={{ position: 'relative' }}>
        <header className="header">
          <div className="logo">
            <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="logo-icon" />
          </div>
          {renderLangButton()}
        </header>
        <Typography className="thank-you-title" sx={{ color: '#f56565' }}>{t('errorTitle')}</Typography>
        <div className="thank-you-error">
          {t('errorMsg')} ({error})
        </div>
        <Button variant="contained" size="large" fullWidth className="home-button" onClick={() => navigate('/')}>
          {t('backToHome')}
        </Button>
      </Container>
    );
  }

  if (!receiptData) return null;

  return (
    <Container maxWidth="sm" className="thank-you-container" sx={{ position: 'relative' }}>
      <header className="header">
        <div className="logo">
          <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="logo-icon" />
        </div>
        {renderLangButton()}
      </header>

      <Typography className="thank-you-title">{t('title')}</Typography>

      <div className="receipt-card">
        <Typography className="receipt-header">{t('receiptHeader')}</Typography>

        <Table className="thank-you-table">
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: '50%' }}>{t('carplateLabel')}</TableCell>
              <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                {receiptData.lpn}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: '50%' }}>{t('entryTimeLabel')}</TableCell>
              <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                {formatDateTime(receiptData.entryTime)}
              </TableCell>
            </TableRow>
            {receiptData.duration && (
              <TableRow>
                <TableCell sx={{ width: '50%' }}>{t('durationLabel')}</TableCell>
                <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                  {formatDuration(receiptData.duration)}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={{ width: '50%' }}>{t('parkingFeeLabel')}</TableCell>
              <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                HK${Number(receiptData.parkingFee).toFixed(2)}
              </TableCell>
            </TableRow>
            {receiptData.paymentMethod && (
              <TableRow>
                <TableCell sx={{ width: '50%' }}>{t('paymentMethodLabel')}</TableCell>
                <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                  {receiptData.paymentMethod}
                </TableCell>
              </TableRow>
            )}
            {receiptData.referenceNumber && (
              <TableRow>
                <TableCell sx={{ width: '50%' }}>{t('referenceNumberLabel')}</TableCell>
                <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                  {receiptData.referenceNumber}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Typography className="thank-you-message">{t('exitMessage')}</Typography>

      {/* <Button
        variant="contained"
        size="large"
        fullWidth
        className="home-button"
        onClick={() => navigate('/')}
      >
        {t('backToHome')}
      </Button> */}
    </Container>
  );
}

export default DThankYou;